import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface RadioCardProps {
  groupName: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
  className?: string;
  children: ReactNode;
}

/**
 * Selectable card row (design §11.1 — "each option: border rounded-lg p-4,
 * selected = border-primary + ring-2 ring-ring"). A native radio input keeps
 * full keyboard semantics (§22/C6, arrows move · space selects) without adding
 * a new Radix primitive; the visible selected state is the border + ring (B5).
 */
export function RadioCard({
  groupName,
  value,
  checked,
  onSelect,
  className,
  children,
}: RadioCardProps) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-foreground/20 motion-reduce:transition-none',
        checked && 'border-primary ring-2 ring-ring',
        className,
      )}
    >
      <input
        type="radio"
        name={groupName}
        value={value}
        checked={checked}
        onChange={() => onSelect(value)}
        className="mt-1 h-4 w-4 shrink-0 accent-primary"
      />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  );
}
